/**
 * Jackpot Wizard Store
 *
 * Multi-step form state for creating/editing jackpots.
 * Handles draft persistence via the jackpot-ops API.
 *
 * Steps: 1) Basic Info → 2) Events → 3) Rounds/Prizes → 4) Review
 */

import { create } from 'zustand';
import { httpClient } from '../api/http-client';

// --- Types ---

export interface JackpotBasicInfo {
  name: string;
  organization_id: string;
  location: string;
  start_date: string;
  end_date: string;
  rodeo_or_jackpot: 'jackpot' | 'rodeo';
}

export interface JackpotEventDraft {
  id?: string;
  event_name: string;
  event_type: string;
  entry_fee: number;
  rounds: JackpotRoundDraft[];
  // Handicap
  handicap_type?: string;
  header_max_handicap?: number;
  heeler_max_handicap?: number;
  max_combined_handicap?: number;
  max_individual_handicap?: number;
  // Round configuration
  total_rounds?: number;
  short_round_size?: number;
  short_round_count?: number;
  elimination_enabled?: boolean;
  // Fees & prizes
  transaction_fee?: number;
  total_prize_amount?: number;
  // Entry limits
  entry_limits?: {
    allow_multiple_entries?: boolean;
    max_entries_per_contestant?: number;
    guaranteed_runs?: number;
  };
  // Slides
  slide_type?: string;
  slide_conversion_rate?: number;
  slide_base_handicap?: number;
  // Registration
  max_contestants?: number;
  /**
   * Default number of contestants that will run in any single performance.
   * Used at draw time to size each performance before overflowing into slack.
   * Optional — when undefined, the draw falls back to ceil(max_contestants /
   * number_of_performances).
   */
  contestants_per_performance?: number;
  registration_deadline?: string;
  allow_no_partner_entry?: boolean;
  allow_multiple_registrations?: boolean;
  max_registrations_per_user?: number;
  /**
   * Team roping only. Forces a contestant's entries to split evenly between
   * Header and Heeler (per-side cap = ceil(max_registrations_per_user / 2)).
   */
  enforce_balanced_positions?: boolean;
  // Pick and Draw
  pick_count?: number;
  draw_count?: number;
  enforce_mutual_picks?: boolean;
  // Flags
  is_century_jackpot?: boolean;
  is_mixed_gender?: boolean;
  // Judge sheet — FK to public.judge_sheet_templates. NULL means use the
  // auto-classified default derived from event_type/discipline.
  judge_sheet_template_id?: string | null;
}

export interface JackpotRoundDraft {
  id?: string;
  round_number: number;
  round_type: string;
  prizes: JackpotPrizeDraft[];
  is_progressive?: boolean;
  contestants_advancing?: number;
  has_prizes?: boolean;
  round_prize_total?: number;
}

export interface JackpotPrizeDraft {
  id?: string;
  place: number;
  amount: number;
  description?: string;
}

/**
 * Jackpot wizard steps:
 *   1 — Basic Info
 *   2 — Events (rounds & prizes are edited inline via EditEventDialog)
 *   3 — Review
 */
export type WizardStep = 1 | 2 | 3;

interface JackpotWizardState {
  // Wizard navigation
  currentStep: WizardStep;
  draftId: string | null;

  // Step 1: Basic Info
  basicInfo: JackpotBasicInfo;

  // Step 2: Events
  events: JackpotEventDraft[];

  // Loading & errors
  isSaving: boolean;
  saveError: string | null;
  isLoading: boolean;
}

interface JackpotWizardActions {
  // Navigation
  setStep: (step: WizardStep) => void;
  nextStep: () => void;
  prevStep: () => void;

  // Step 1
  setBasicInfo: (info: Partial<JackpotBasicInfo>) => void;

  // Step 2: Events
  addEvent: (event: JackpotEventDraft) => void;
  updateEvent: (index: number, event: Partial<JackpotEventDraft>) => void;
  removeEvent: (index: number) => void;

  // Step 2/3: Rounds within an event
  addRound: (eventIndex: number, round: JackpotRoundDraft) => void;
  updateRound: (
    eventIndex: number,
    roundIndex: number,
    round: Partial<JackpotRoundDraft>,
  ) => void;
  removeRound: (eventIndex: number, roundIndex: number) => void;

  // Step 3: Prizes within a round
  addPrize: (
    eventIndex: number,
    roundIndex: number,
    prize: JackpotPrizeDraft,
  ) => void;
  updatePrize: (
    eventIndex: number,
    roundIndex: number,
    prizeIndex: number,
    prize: Partial<JackpotPrizeDraft>,
  ) => void;
  removePrize: (
    eventIndex: number,
    roundIndex: number,
    prizeIndex: number,
  ) => void;

  // Draft persistence
  saveDraft: () => Promise<void>;
  loadDraft: (jackpotId: string) => Promise<void>;

  // Reset
  resetWizard: () => void;
}

type JackpotStore = JackpotWizardState & JackpotWizardActions;

const initialBasicInfo: JackpotBasicInfo = {
  name: '',
  organization_id: '',
  location: '',
  start_date: '',
  end_date: '',
  rodeo_or_jackpot: 'jackpot',
};

const initialState: JackpotWizardState = {
  currentStep: 1,
  draftId: null,
  basicInfo: { ...initialBasicInfo },
  events: [],
  isSaving: false,
  saveError: null,
  isLoading: false,
};

export const useJackpotStore = create<JackpotStore>()((set, get) => ({
  ...initialState,

  // --- Navigation ---
  setStep: (step) => set({ currentStep: step }),

  nextStep: () => {
    const { currentStep } = get();
    if (currentStep < 3) set({ currentStep: (currentStep + 1) as WizardStep });
  },

  prevStep: () => {
    const { currentStep } = get();
    if (currentStep > 1) set({ currentStep: (currentStep - 1) as WizardStep });
  },

  // --- Step 1: Basic Info ---
  setBasicInfo: (info) =>
    set((state) => ({
      basicInfo: { ...state.basicInfo, ...info },
    })),

  // --- Step 2: Events ---
  addEvent: (event) =>
    set((state) => ({
      events: [...state.events, { ...event, id: event.id || crypto.randomUUID() }],
    })),

  updateEvent: (index, event) =>
    set((state) => {
      const events = [...state.events];
      events[index] = { ...events[index], ...event };
      return { events };
    }),

  removeEvent: (index) =>
    set((state) => ({
      events: state.events.filter((_, i) => i !== index),
    })),

  // --- Rounds ---
  addRound: (eventIndex, round) =>
    set((state) => {
      const events = [...state.events];
      events[eventIndex] = {
        ...events[eventIndex],
        rounds: [...events[eventIndex].rounds, round],
      };
      return { events };
    }),

  updateRound: (eventIndex, roundIndex, round) =>
    set((state) => {
      const events = [...state.events];
      const rounds = [...events[eventIndex].rounds];
      rounds[roundIndex] = { ...rounds[roundIndex], ...round };
      events[eventIndex] = { ...events[eventIndex], rounds };
      return { events };
    }),

  removeRound: (eventIndex, roundIndex) =>
    set((state) => {
      const events = [...state.events];
      events[eventIndex] = {
        ...events[eventIndex],
        rounds: events[eventIndex].rounds.filter((_, i) => i !== roundIndex),
      };
      return { events };
    }),

  // --- Prizes ---
  addPrize: (eventIndex, roundIndex, prize) =>
    set((state) => {
      const events = [...state.events];
      const rounds = [...events[eventIndex].rounds];
      rounds[roundIndex] = {
        ...rounds[roundIndex],
        prizes: [...rounds[roundIndex].prizes, prize],
      };
      events[eventIndex] = { ...events[eventIndex], rounds };
      return { events };
    }),

  updatePrize: (eventIndex, roundIndex, prizeIndex, prize) =>
    set((state) => {
      const events = [...state.events];
      const rounds = [...events[eventIndex].rounds];
      const prizes = [...rounds[roundIndex].prizes];
      prizes[prizeIndex] = { ...prizes[prizeIndex], ...prize };
      rounds[roundIndex] = { ...rounds[roundIndex], prizes };
      events[eventIndex] = { ...events[eventIndex], rounds };
      return { events };
    }),

  removePrize: (eventIndex, roundIndex, prizeIndex) =>
    set((state) => {
      const events = [...state.events];
      const rounds = [...events[eventIndex].rounds];
      rounds[roundIndex] = {
        ...rounds[roundIndex],
        prizes: rounds[roundIndex].prizes.filter((_, i) => i !== prizeIndex),
      };
      events[eventIndex] = { ...events[eventIndex], rounds };
      return { events };
    }),

  // --- Draft Persistence ---
  saveDraft: async () => {
    const { draftId, basicInfo, events } = get();
    set({ isSaving: true, saveError: null });
    try {
      const payload = {
        name: basicInfo.name,
        primary_organization_id: basicInfo.organization_id,
        start_date: basicInfo.start_date || undefined,
        end_date: basicInfo.end_date || undefined,
        events: events.length ? events.map((ev) => ({
          ...(ev.id ? { id: ev.id } : {}),
          event_name: ev.event_name,
          entry_fee: ev.entry_fee,
          handicap_type: ev.handicap_type || undefined,
          header_max_handicap: ev.header_max_handicap,
          heeler_max_handicap: ev.heeler_max_handicap,
          max_combined_handicap: ev.max_combined_handicap,
          max_individual_handicap: ev.max_individual_handicap,
          total_rounds: ev.total_rounds,
          short_round_size: ev.short_round_size,
          short_round_count: ev.short_round_count,
          elimination_enabled: ev.elimination_enabled,
          transaction_fee: ev.transaction_fee,
          total_prize_amount: ev.total_prize_amount,
          entry_limits: ev.entry_limits,
          slide_type: ev.slide_type,
          slide_conversion_rate: ev.slide_conversion_rate,
          slide_base_handicap: ev.slide_base_handicap,
          is_century_jackpot: ev.is_century_jackpot,
          is_mixed_gender: ev.is_mixed_gender,
          max_contestants: ev.max_contestants,
          contestants_per_performance: ev.contestants_per_performance,
          registration_deadline: ev.registration_deadline,
          allow_no_partner_entry: ev.allow_no_partner_entry,
          allow_multiple_registrations: ev.allow_multiple_registrations,
          max_registrations_per_user: ev.max_registrations_per_user,
          enforce_balanced_positions: ev.enforce_balanced_positions,
          pick_count: ev.pick_count,
          draw_count: ev.draw_count,
          enforce_mutual_picks: ev.enforce_mutual_picks,
          rounds: ev.rounds?.length
            ? ev.rounds.map((r, i) => ({
                round_number: r.round_number ?? i + 1,
                is_progressive: r.is_progressive,
                contestants_advancing: r.contestants_advancing,
                has_prizes: r.has_prizes,
                round_prize_total: r.round_prize_total,
                prizes: r.prizes?.map((p) => ({
                  place: p.place,
                  percentage: (p as any).percentage ?? 0,
                })),
              }))
            : undefined,
        })) : undefined,
      };
      // Strip undefined values — TypeBox rejects explicit undefined for optional fields
      const clean = JSON.parse(JSON.stringify(payload));
      if (draftId) {
        await httpClient.put(`/jackpot-ops/draft/${draftId}`, clean);
      } else {
        const result = await httpClient.post<{ id: string }>(
          '/jackpot-ops/draft',
          clean,
        );
        set({ draftId: result.id });
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to save draft';
      set({ saveError: msg });
    } finally {
      set({ isSaving: false });
    }
  },

  loadDraft: async (jackpotId) => {
    set({ isLoading: true, saveError: null });
    try {
      const data = await httpClient.get<{
        id: string;
        name: string;
        organization_id: string;
        location?: string;
        start_date?: string;
        end_date?: string;
        rodeo_or_jackpot?: string;
        events?: JackpotEventDraft[];
      }>(`/jackpot-ops/${jackpotId}`);

      set({
        draftId: data.id,
        basicInfo: {
          name: data.name || '',
          organization_id: data.organization_id || '',
          location: data.location || '',
          start_date: data.start_date || '',
          end_date: data.end_date || '',
          rodeo_or_jackpot:
            (data.rodeo_or_jackpot as 'jackpot' | 'rodeo') || 'jackpot',
        },
        events: (data.events || []).map((e) => ({
          ...e,
          rounds: (e.rounds || []).map((r) => ({
            ...r,
            prizes: r.prizes || [],
          })),
        })),
        currentStep: 1,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load draft';
      set({ saveError: msg });
    } finally {
      set({ isLoading: false });
    }
  },

  // --- Reset ---
  resetWizard: () => set({ ...initialState }),
}));

// Selector hooks
export const useCurrentStep = () =>
  useJackpotStore((s) => s.currentStep);
export const useJackpotBasicInfo = () =>
  useJackpotStore((s) => s.basicInfo);
export const useJackpotEvents = () =>
  useJackpotStore((s) => s.events);
export const useJackpotSaving = () =>
  useJackpotStore((s) => ({ isSaving: s.isSaving, saveError: s.saveError }));
